import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Badge from '../components/ui/Badge';
import Avatar from '../components/ui/Avatar';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import PhotoPlaceholder from '../components/ui/PhotoPlaceholder';

function ChevronLeft() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg className="w-3.5 h-3.5 shrink-0 text-terra-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  );
}


export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [guestModalOpen, setGuestModalOpen] = useState(false);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/listings/${id}`)
      .then(res => setListing(res.data.listing))
      .catch(() => navigate('/listings'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const provider = listing?.profiles;
  const isOwner = user?.id === provider?.id;
  const allPhotos = listing ? [listing.cover_photo_url, ...(listing.photos || [])].filter(Boolean) : [];

  const goToOrderForm = () => navigate(`/orders/new?listing=${listing.id}`);
  const handleContactClick = () => {
    if (user) return goToOrderForm();
    setGuestModalOpen(true);
  };
  const goToLogin = () => navigate('/login', {
    state: { from: { pathname: '/orders/new', search: `?listing=${listing.id}` } },
  });

  if (loading) {
    return (
      <div className="bg-sand-50 min-h-screen flex items-center justify-center">
        <div role="status" aria-label="Loading" className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="bg-sand-50 min-h-screen flex items-center justify-center">
        <p className="font-heading text-lg text-ink-900">Listing not found</p>
      </div>
    );
  }

  return (
    <div className="bg-sand-50 min-h-screen pb-24 sm:pb-0">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">

        {/* Back nav */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-ink-400 hover:text-ink-900 transition-colors"
          >
            <ChevronLeft />
            Back
          </button>
          <span className="text-sand-300 select-none">·</span>
          <Badge variant="category">{listing.category}</Badge>
        </div>

        {/* Title */}
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink-900 mb-8 max-w-3xl leading-tight">
          {listing.title}
        </h1>

        {/* Content + sticky card */}
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* Left: photos + description + location */}
          <div className="flex-1 min-w-0">

            {/* Photo gallery */}
            <div className="mb-8">
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-sand-100 mb-3">
                {allPhotos.length > 0
                  ? <img src={allPhotos[activePhoto]} alt={listing.title} className="w-full h-full object-cover" />
                  : <PhotoPlaceholder />
                }
              </div>
              {allPhotos.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {allPhotos.map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActivePhoto(i)}
                      className={[
                        'w-16 h-16 rounded-lg overflow-hidden shrink-0 border-2 transition-all',
                        activePhoto === i
                          ? 'border-terra-400'
                          : 'border-transparent opacity-60 hover:opacity-100',
                      ].join(' ')}
                    >
                      <img src={p} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {listing.description ? (
              <section className="mb-8">
                <h2 className="font-heading text-base font-semibold text-ink-900 mb-3">About this service</h2>
                <p className="text-sm text-ink-500 leading-relaxed whitespace-pre-wrap">{listing.description}</p>
              </section>
            ) : null}

            {listing.location && (
              <>
                <div className="border-t border-sand-200 mb-8" />
                <section className="mb-8">
                  <h2 className="font-heading text-base font-semibold text-ink-900 mb-3">Location</h2>
                  <div className="flex items-center gap-2 text-sm text-ink-500">
                    <PinIcon />
                    {listing.location}
                  </div>
                </section>
              </>
            )}

            {/* Provider section — mobile only */}
            <div className="lg:hidden">
              <div className="border-t border-sand-200 mb-8" />
              <section className="mb-8">
                <h2 className="font-heading text-base font-semibold text-ink-900 mb-4">About the provider</h2>
                <div className="flex items-center gap-3 mb-3">
                  <Avatar
                    src={provider?.avatar_url}
                    name={`${provider?.first_name || ''} ${provider?.last_name || ''}`}
                    size="md"
                  />
                  <div>
                    <p className="text-sm font-semibold text-ink-900">{provider?.first_name} {provider?.last_name}</p>
                    {provider?.location && <p className="text-xs text-ink-400">{provider.location}</p>}
                  </div>
                </div>
                {provider?.bio && (
                  <p className="text-sm text-ink-500 leading-relaxed">{provider.bio}</p>
                )}
              </section>
            </div>
          </div>

          {/* Right: sticky action card */}
          <div className="w-full lg:w-80 xl:w-96 shrink-0">
            <div className="sticky top-8 bg-white border border-sand-200 rounded-2xl overflow-hidden shadow-sm">

              {/* Price */}
              {listing.price && (
                <div className="px-6 pt-6 pb-5 border-b border-sand-100">
                  <p className="text-xs font-medium text-ink-400 uppercase tracking-wide mb-1">Price</p>
                  <p className="font-heading text-3xl font-semibold text-ink-900">AED <span>{listing.price}</span></p>
                </div>
              )}

              {/* CTA */}
              <div className="px-6 py-5 border-b border-sand-100">
                {isOwner
                  ? <p className="text-xs text-ink-400 text-center py-1">This is your listing</p>
                  : (
                      <Button
                        variant="primary"
                        className="w-full justify-center"
                        onClick={handleContactClick}
                      >
                        Contact Provider
                      </Button>
                    )
                }
              </div>

              {/* Provider */}
              <div className="px-6 py-5">
                <p className="text-xs font-medium text-ink-400 uppercase tracking-wide mb-4">Provider</p>
                <div className="flex items-center gap-3 mb-3">
                  <Avatar
                    src={provider?.avatar_url}
                    name={`${provider?.first_name || ''} ${provider?.last_name || ''}`}
                    size="md"
                  />
                  <div>
                    <p className="text-sm font-semibold text-ink-900">{provider?.first_name} {provider?.last_name}</p>
                    {provider?.location && <p className="text-xs text-ink-400">{provider.location}</p>}
                  </div>
                </div>
                {provider?.bio && (
                  <p className="text-xs text-ink-400 leading-relaxed line-clamp-4">{provider.bio}</p>
                )}
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Mobile fixed CTA */}
      {!isOwner && (
        <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-sand-200 px-4 py-3 sm:hidden">
          <div className="flex items-center gap-3">
            {listing.price && (
              <div className="shrink-0">
                <p className="text-xs text-ink-400">Price</p>
                <p className="font-heading text-lg font-semibold text-ink-900">AED {listing.price}</p>
              </div>
            )}
            <div className="flex-1">
              <Button
                variant="primary"
                className="w-full justify-center"
                onClick={handleContactClick}
              >
                Contact Provider
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={guestModalOpen}
        onClose={() => setGuestModalOpen(false)}
        title="Sign in or continue as guest"
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-ink-400">
            Sign in for a faster checkout and to track all your requests, or continue without an account.
          </p>
          <Button variant="primary" className="w-full justify-center" onClick={goToLogin}>
            Sign in to order
          </Button>
          <Button variant="secondary" className="w-full justify-center" onClick={goToOrderForm}>
            Continue as guest
          </Button>
        </div>
      </Modal>
    </div>
  );
}
